import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Tooltip, CircularProgress, Typography } from '@mui/material'
import { PersonSearch, AlternateEmail, MailOutline, Send } from '@mui/icons-material'
import DartIcon from '../../mailTemplate/src/components/atomic/atoms/DartIcon'
import { useGlobalStoreData } from '../../store/globalStore'

const mapB2bLimits = (b2bLimit) => {
  if (!b2bLimit) return null
  return {
    relevancyCheck: { used: b2bLimit.b2bUsedRelevancyCheckCountLimit || 0, total: b2bLimit.b2bRelevancyCheckCountLimit ?? 0 },
    contactSearch: { used: b2bLimit.b2bUsedContactFirmSearchCountLimit || 0, total: b2bLimit.b2bContactFirmSearchCountLimit ?? 0 },
    emailQuery: { used: b2bLimit.b2bUsedContactEmailSearchCountLimit || 0, total: b2bLimit.b2bContactEmailSearchCountLimit ?? 0 },
    emailGeneration: { used: b2bLimit.b2bUsedEmailContentGenerateCountLimit || 0, total: b2bLimit.b2bEmailContentGenerateCountLimit ?? 0 },
    sentEmails: { used: b2bLimit.b2bUsedTotalEmailLimit || 0, total: b2bLimit.b2bTotalEmailLimit ?? 0 }
  }
}

const LIMIT_CONFIGS = [
  { key: 'relevancyCheck', icon: DartIcon, color: '#7C4DFF', labelKey: 'mailTemplate.campaigns.dashboard.relevancyCheck' },
  { key: 'contactSearch', icon: PersonSearch, color: '#0097A7', labelKey: 'mailTemplate.campaigns.dashboard.contactSearch' },
  { key: 'emailQuery', icon: AlternateEmail, color: '#E65100', labelKey: 'mailTemplate.campaigns.dashboard.emailAddressQuery' },
  { key: 'emailGeneration', icon: MailOutline, color: '#2E7D32', labelKey: 'mailTemplate.campaigns.dashboard.generateContent' },
  { key: 'sentEmails', icon: Send, color: '#1565C0', labelKey: 'mailTemplate.campaigns.dashboard.sentEmails' }
]

const CIRCLE_SIZE = 44

const LimitCircle = ({ icon: Icon, used, total, color, label }) => {
  const isUnlimited = total === -1
  const percentage = isUnlimited
    ? 0
    : total > 0
      ? Math.min((used / total) * 100, 100)
      : 0
  const isNearLimit = percentage >= 80
  const isAtLimit = percentage >= 100

  const activeColor = isAtLimit
    ? '#d32f2f'
    : isNearLimit
      ? '#ed6c02'
      : color

  const detail = isUnlimited ? `${used}/∞` : `${used}/${total}`

  return (
    <Tooltip
      title={`${label}: ${detail}`}
      arrow
      placement="top"
    >
      <Box sx={{ position: 'relative', width: CIRCLE_SIZE, height: CIRCLE_SIZE }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={CIRCLE_SIZE}
          thickness={3}
          sx={{ color: `${activeColor}20`, position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={CIRCLE_SIZE}
          thickness={3}
          sx={{ color: activeColor, position: 'absolute' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ fontSize: 18, color: activeColor }} />
        </Box>
      </Box>
    </Tooltip>
  )
}

const CampaignLimits = ({ limits: limitsProp, b2bAccountId }) => {
  const { t } = useTranslation()
  const user = useGlobalStoreData.user()

  const limits = useMemo(() => {
    if (limitsProp) return mapB2bLimits(limitsProp)
    const accountLimit = b2bAccountId
      ? user?.followedB2bAccountIds?.[b2bAccountId]?.limit
      : null
    return mapB2bLimits(accountLimit || user?.limit)
  }, [limitsProp, b2bAccountId, user?.followedB2bAccountIds, user?.limit])

  if (!limits) return null

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          fontWeight: 600,
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.75,
          display: 'block',
          textAlign: 'center'
        }}
      >
        {t('limit.header')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
        {LIMIT_CONFIGS.map((config) => {
          const data = limits[config.key]
          if (!data) return null
          return (
            <LimitCircle
              key={config.key}
              icon={config.icon}
              used={data.used}
              total={data.total}
              color={config.color}
              label={t(config.labelKey)}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default CampaignLimits
